
import WDElement from '../wd-element.js';
import { WDProxyArray } from '../../core/wd-proxy';
import WDButton from '../button/wd-button.js';

export default class WDOptionList extends WDElement
{
	#options;				//our array of options for the list
	#origReqURI;
	#proxy;					//in case of template uris, resolve templates against this
	#fetchTimeout;
	#config = {
		filterKey: 'search_string',
		valueField: 'value',
		options: [],
		templates: {
			label: '${label}',
			icon: null,
		}
	};

	#isFetching = false;

	constructor(cfg)
	{
		super();

		if (cfg) Object.assign(this.#config, cfg);

		this.tabIndex = -1;
		this.#options = new WDProxyArray([]);

		//setup an event watcher for proxy set if we have a content handler
		this.#options.on('set', evtData => this.handleSet(evtData));
		this.#options.on('delete', evtData => this.handleDelete(evtData));
		
		if (this.#config.proxy) 
		{
			this.proxy = this.#config.proxy;
		}

		//if passed a fetch
		if (this.#config.fetch) 
		{
			this.initFetch();
		}
		else
		{
			//off main loop so any subclass can watch for options:set in the constructor
			setTimeout(() => this.options = this.#config.options , 1);
		}

		return this;
	};

	//accessors
	get config() { return this.#config; };
	get items() { return this.children; };
	get itemArray() { return [...this.items]; };
	get firstItem() { return this.items[0]; };
	get lastItem() { return this.items[this.items.length-1]; };
	get isFetching() { 
		return this.#isFetching; 
	};

	get options() { return this.#options; };
	set options(newOptions)
	{
		this.#options.replace(newOptions);
		this.trigger('options:set',{options:newOptions},true);
	};

	reset()
	{
		this.options = [];
		this.replaceChildren();
	}

	set proxy(p) { 
		this.#proxy = p; 
	};

	get fetch() { return this.#config.fetch; };
	set fetch(f)
	{
		this.#config.fetch = f;
		this.initFetch();
	};

	set fetchURI(uri)
	{
		this.fetch.setReqURI(uri);
		this.initFetch();
	};

	initFetch()
	{
		if (this.fetch)
		{
			this.#origReqURI = this.fetch.getReqURI();
			this.addWatchers();
			this.fetchOptions();
		}
		else
		{
			this.options = [];
		}
		
	};

	isURIValid(uri)
	{
		return (!uri.includes('${') &&
			!uri.includes('//') &&
			!uri.includes('/undefined') &&
			!uri.includes('/null')
		);
	};

	resolveReqURI()
	{
		if (!this.fetch) return false;

		let uri = this.#origReqURI;

		if (this.#proxy && uri.includes('${'))
		{
			uri = uri.interpolate(this.#proxy);
		}

		if (this.isURIValid(uri))
		{
			this.fetch.setReqURI(uri);
			return true;
		}
		
		return false;
	};

	addWatchers()
	{
		if (!this.#proxy) return;

		const watchKeys = this.#origReqURI.getTemplateKeys();

		if (watchKeys.length > 0)
		{
			this.#proxy.onSet(watchKeys,(evt) => {

				const oldVal = evt?.prev_value?.value;
				const newVal = evt?.value?.value;

				if (oldVal != newVal)
				{
					this.fetchOptions();
				}

		 	});
		}
	};

	fetchOptions(withDelay)
	{
		if (this.resolveReqURI())
		{
			this.trigger('options:fetch');

			if (this.#fetchTimeout) clearTimeout(this.#fetchTimeout);

			const timeout = withDelay ? 150 : 10;
			this.#isFetching = true;				

			this.#fetchTimeout = setTimeout( () => {

				return this.fetch.req().then(results =>
				{
					if (results instanceof Array) this.options = results;
					else console.error('WDOptionList fetch did not return array', results);

					this.#isFetching = false;
					this.trigger('options:fetched');

				});

			}, timeout);

		}
		else
		{
			return new Promise((resolve, reject) =>
			{
				this.options = [];
				resolve(true);
			});
		}

	};

	handleSet(evtData)
	{
		/**
		  create/replace a row with this index
		  */
		const idx = evtData.property;
		const data = evtData.value;

		//if the property isn't numeric, then it's an internal array property we don't want
		if (parseInt(idx) != idx) return;

		//if data is undefined, the item is being removed
		if (data === undefined)
		{
			this.removeItemAt(idx);
		}
		//not sure this handles replacing a row
		else
		{
			/*
			let item = null;
			
			if (typeof(data) == 'object' && data[this.#config.valueField] !== undefined)
			{
				const valueToMatch = data[this.#config.valueField];
				item = this.itemArray.find(existingItem =>
					existingItem.model[this.#config.valueField] === valueToMatch
				);
			}
			else
			{
				const valueToMatch = data[this.#config.valueField];
				item = this.itemArray.find(existingItem => existingItem?.model === data);
			}
			
			// If no existing item found (or valueField not set), create new item
			if (!item)
			{
				item = this.insertItemAt(idx);
			}
			*/

			const item = this.insertItemAt(idx);
			item.model = data;
			this.setItemContent(item, data);	
			
		}
	};

	setItemContent(item,data)
	{
		item.label = this.#config.templates.label.interpolate(data);

		if (this.#config.templates?.icon)
		{
			const iconTmpl = this.#config.templates.icon;

			//if given a template, interpolate it otherwise just use as an icon
			item.icon = iconTmpl.includes('${') ? iconTmpl.interpolate(data) : iconTmpl;
		}
	};

	handleDelete(evtData)
	{
		const idx = evtData.property;

		//if the property isn't numeric, then it's an internal array property we don't want
		if (parseInt(idx) != idx) return;

		this.removeItemAt(idx);
	};

	add(params)
	{
		const item = this.insertItemAt(this.items.length);
		if (params) item.addConfig(params);

		return item;
	};

	createItem(itemElem)
	{
		//allow overrides of the list element from subclasses
		const item = itemElem ? itemElem : new WDButton();

		item.classList.remove('btn');
		item.tabIndex = '-1';		//so we can manually focus on a list item

		this.addItemEvents(item);

		return item;
	};

	addItemEvents(item)
	{
		//watch for changes
		item.on('click', (evt) =>
		{
			this.trigger('item:selected', item);
		});

		// Keyboard navigation
		item.on('keydown', (e) =>
		{
			if (e.key === 'Enter') 
			{
				e.preventDefault();	//stops the keyboard from beeping on enter
				this.trigger('item:selected', item);
			}
			else if (e.key == 'ArrowDown' || e.key == 'ArrowUp')
			{
				e.preventDefault();
			}
		});
	};

	insertItemAt(idx)
	{
		const item = this.createItem();

		if (idx === 0) 
		{
			this.prepend(item);
		}
		else if (idx < this.items.length)
		{
			this.items[idx].before(item);
		}
		else
		{
			this.append(item);
		}

		return item;
	};

	/**
	 * still used???
	 */
	replaceItemAt(idx)
	{
		const item = this.createItem();

		if (idx < this.items.length)
		{
			const r = this.items[idx];

			r.before(item);
			r.remove();
		}
		else
		{
			this.append(item);
		}

		return item;
	};

	removeItemAt(idx)
	{
		if (typeof (this.items[idx]) != 'undefined')
		{
			this.items[idx].remove();
		}
	};

	removeItem(item)
	{
		item.remove();
	};

	removeItems(arr)
	{
		arr.forEach(item => item.remove());
	};

	filter(str)
	{
		if (this.fetch)
		{
			const fk = this.#config.filterKey;
			if (str.length > 0)
			{
				this.fetch[fk] = str;
			}
			else
			{
				delete(this.fetch[fk]);
			}

			this.fetchOptions(true);
		}
		else if (this.options.length > 0)
		{
			const ss = str.toLowerCase();

			this.options.forEach((btn, idx) =>
			{
				const labelStr = btn.label.innerText.toLowerCase();

				if (ss.length == '0' || labelStr.includes(ss))
				{
					btn.style.display = 'flex';
				}
				else
				{
					btn.style.display = 'none';
				}
			});
		}
	};

};

customElements.define('wd-option-list', WDOptionList);

